import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import "./Configuration.css";

const POSCashDrawerSettings = () => {
  const { Get, Post, Patch, Delete, getAPI } = useApi();
  const { user } = useContext(AuthContext);
  const storeId = user?.store;

  const [drawers, setDrawers] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testingId, setTestingId] = useState(null);

  // Form State
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [connectionType, setConnectionType] = useState("PRINTER_DRIVEN");
  const [ipAddress, setIpAddress] = useState("");
  const [port, setPort] = useState("");
  const [printerId, setPrinterId] = useState("");
  const [triggerCommand, setTriggerCommand] = useState("27,112,0,25,250");

  const fetchData = async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const drawerData = await Get(`${getAPI('posCashDrawers')}?store_id=${storeId}`);
      setDrawers(drawerData || []);

      const printerData = await Get(`${getAPI('posPrinters')}?store_id=${storeId}`);
      setPrinters(printerData || []);
    } catch (error) {
      console.error("Error fetching drawers data:", error);
      toast.error("Failed to load cash drawers configurations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [storeId]);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setConnectionType("PRINTER_DRIVEN");
    setIpAddress("");
    setPort("");
    setPrinterId("");
    setTriggerCommand("27,112,0,25,250");
    setShowForm(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Drawer name is required.");
      return;
    }

    const payload = {
      store_id: storeId,
      name,
      connection_type: connectionType,
      ip_address: connectionType === "USB" ? null : ipAddress || null,
      port: connectionType === "USB" ? null : port ? Number(port) : null,
      printer_id: connectionType === "PRINTER_DRIVEN" ? printerId : null,
      trigger_command: triggerCommand,
    };

    try {
      if (editingId) {
        await Patch("posCashDrawers", editingId, payload);
        toast.success("Cash drawer updated successfully!");
      } else {
        await Post("posCashDrawers", payload);
        toast.success("Cash drawer registered successfully!");
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving cash drawer:", error);
      toast.error(error.response?.data?.detail || "Failed to save cash drawer.");
    }
  };

  const handleEdit = (d) => {
    setEditingId(d.id);
    setName(d.name);
    setConnectionType(d.connection_type);
    setIpAddress(d.ip_address || "");
    setPort(String(d.port || ""));
    setPrinterId(d.printer_id || "");
    setTriggerCommand(d.trigger_command || "27,112,0,25,250");
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to unregister this cash drawer?")) return;
    try {
      await Delete("posCashDrawers", id);
      toast.success("Cash drawer unregistered.");
      fetchData();
    } catch (error) {
      console.error("Error deleting drawer:", error);
      toast.error("Failed to delete cash drawer.");
    }
  };

  const handleKickDrawer = async (d) => {
    setTestingId(d.id);
    toast.info(`Sending trigger pulse to open ${d.name}...`);
    try {
      if (d.connection_type === "PRINTER_DRIVEN") {
        const selectedPrinter = printers.find(p => p.id === d.printer_id);
        if (selectedPrinter && selectedPrinter.connection_type === "NETWORK") {
          // Direct network print kick signal
          const kickUrl = `http://${selectedPrinter.ip_address}:${selectedPrinter.port || 9100}/kick-drawer`;
          await fetch(kickUrl, { method: "POST", mode: "no-cors" });
          toast.success("Drawer kick: Command dispatched via network printer!");
        } else {
          // Simulated local/browser drawer pop
          toast.success("Drawer kick: Local printer kick signal simulation successful!");
        }
      } else {
        // Direct USB drawer trigger simulation
        toast.success("Drawer kick: USB drawer signal simulation successful!");
      }
    } catch (err) {
      console.error("Drawer kick failed:", err);
      toast.error("Drawer kick: Device offline or direct trigger block");
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="responsive-box store-config-panel" style={{ marginTop: "20px" }}>
      <div className="store-config-header">
        <div>
          <span className="store-config-kicker">Device Integration</span>
          <h2>Cash Drawers</h2>
          <p>Register cash registers, kick-drawer trigger codes, and receipt-printer relays.</p>
        </div>
        {!showForm && (
          <button
            type="button"
            className="store-config-save"
            style={{ minWidth: "150px" }}
            onClick={() => setShowForm(true)}
          >
            + Add Drawer
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSave} style={{ marginBottom: "30px" }}>
          <div className="store-config-header">
            <h3>{editingId ? "Edit Cash Drawer" : "Register Cash Drawer"}</h3>
          </div>

          <div className="store-config-grid">
            <div className="store-config-field">
              <div>
                <label htmlFor="drawer-name">Drawer Name *</label>
                <span>Descriptive label (e.g. Register Drawer 1).</span>
              </div>
              <input
                id="drawer-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Front Cash Drawer"
                required
              />
            </div>

            <div className="store-config-field">
              <div>
                <label htmlFor="drawer-connection">Connection Type</label>
                <span>Trigger source (driven by printer or direct USB).</span>
              </div>
              <select
                id="drawer-connection"
                value={connectionType}
                onChange={(e) => setConnectionType(e.target.value)}
              >
                <option value="PRINTER_DRIVEN">Receipt Printer Driven (RJ12)</option>
                <option value="USB">Direct USB Drawer</option>
                <option value="SERIAL">Serial / COM Port</option>
              </select>
            </div>

            {connectionType === "PRINTER_DRIVEN" && (
              <div className="store-config-field">
                <div>
                  <label htmlFor="drawer-printer">Receipt Printer Relay</label>
                  <span>Select the printer that kicks this drawer.</span>
                </div>
                <select
                  id="drawer-printer"
                  value={printerId}
                  onChange={(e) => setPrinterId(e.target.value)}
                  required
                >
                  <option value="">-- Choose Printer --</option>
                  {printers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.connection_type})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {connectionType !== "PRINTER_DRIVEN" && connectionType !== "USB" && (
              <>
                <div className="store-config-field">
                  <div>
                    <label htmlFor="drawer-ip">Drawer COM Port or IP</label>
                    <span>COM port or IP address.</span>
                  </div>
                  <input
                    id="drawer-ip"
                    type="text"
                    value={ipAddress}
                    onChange={(e) => setIpAddress(e.target.value)}
                    placeholder="e.g. COM1 or 192.168.1.110"
                    required
                  />
                </div>

                <div className="store-config-field">
                  <div>
                    <label htmlFor="drawer-port">Port</label>
                    <span>Socket port if IP-based.</span>
                  </div>
                  <input
                    id="drawer-port"
                    type="number"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    placeholder="9100"
                  />
                </div>
              </>
            )}

            <div className="store-config-field">
              <div>
                <label htmlFor="drawer-cmd">Kick Command (Decimal ESC/POS)</label>
                <span>Comma separated decimal byte commands.</span>
              </div>
              <input
                id="drawer-cmd"
                type="text"
                value={triggerCommand}
                onChange={(e) => setTriggerCommand(e.target.value)}
                placeholder="27,112,0,25,250"
              />
            </div>
          </div>

          <div className="store-config-actions" style={{ gap: "12px", justifyContent: "flex-start", marginTop: "24px" }}>
            <button type="submit" className="store-config-save">
              Save Drawer
            </button>
            <button
              type="button"
              className="store-config-reset"
              onClick={resetForm}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ color: "#64748b", fontSize: "15px" }}>Loading cash drawers...</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "20px", marginTop: "20px" }}>
          {drawers.map((d) => (
            <div
              key={d.id}
              className="store-config-field"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "14px",
                padding: "20px",
                height: "100%",
                justifyContent: "space-between"
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "850", color: "#0f172a" }}>📥 {d.name}</h4>
                  <span
                    style={{
                      fontSize: "12px",
                      background: d.enabled ? "#eefbf2" : "#fdf2f2",
                      color: d.enabled ? "#1f8a4c" : "#9b1c1c",
                      padding: "4px 10px",
                      borderRadius: "999px",
                      fontWeight: "800",
                    }}
                  >
                    {d.enabled ? "Active" : "Disabled"}
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>
                    <strong>Trigger Source:</strong> {d.connection_type === "PRINTER_DRIVEN" ? `Printer-Driven (${printers.find(p => p.id === d.printer_id)?.name || "Unknown Printer"})` : d.connection_type}
                  </span>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>
                    <strong>Kick Command:</strong> `{d.trigger_command}`
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                <button
                  onClick={() => handleKickDrawer(d)}
                  disabled={testingId === d.id}
                  className="store-config-save"
                  style={{
                    flex: 1,
                    minWidth: "auto",
                    padding: "8px 12px",
                    fontSize: "13px",
                    background: testingId === d.id ? "#94a3b8" : "#2563eb",
                    boxShadow: "none"
                  }}
                >
                  {testingId === d.id ? "Opening..." : "Pop Drawer"}
                </button>
                <button
                  onClick={() => handleEdit(d)}
                  className="store-config-reset"
                  style={{
                    minWidth: "auto",
                    padding: "8px 16px",
                    fontSize: "13px",
                    boxShadow: "none"
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(d.id)}
                  className="store-config-reset"
                  style={{
                    minWidth: "auto",
                    padding: "8px 16px",
                    fontSize: "13px",
                    background: "#fef2f2",
                    color: "#b91c1c",
                    boxShadow: "none"
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}

          {drawers.length === 0 && !showForm && (
            <div
              style={{
                gridColumn: "1/-1",
                textAlign: "center",
                color: "#64748b",
                padding: "40px 0",
                fontSize: "15px"
              }}
            >
              No cash drawers configured. Click "+ Add Drawer" to link your register drawer.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default POSCashDrawerSettings;
